import { useEffect, useMemo, useState } from 'react';

import { apiGet, apiPost } from '../lib/api';

interface WiseTx {
  id: string;
  reference: string | null;
  sender_name: string | null;
  amount: number;
  currency: string;
  occurred_at: string;
}

interface InvoiceOption {
  id: string;
  invoice_number: string;
  status: 'sent' | 'overdue' | 'draft' | 'paid' | 'void';
  total: number;
  currency: string;
}

export function ReconciliationPage() {
  const [transactions, setTransactions] = useState<WiseTx[]>([]);
  const [invoices, setInvoices] = useState<InvoiceOption[]>([]);
  const [selectedInvoices, setSelectedInvoices] = useState<Record<string, string>>({});
  const [busyTxId, setBusyTxId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [txData, sentData, overdueData] = await Promise.all([
          apiGet<WiseTx[]>('/api/wise/transactions?limit=100'),
          apiGet<InvoiceOption[]>('/api/invoices?status=sent'),
          apiGet<InvoiceOption[]>('/api/invoices?status=overdue'),
        ]);
        setTransactions(txData);
        setInvoices([...sentData, ...overdueData]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load reconciliation data');
      }
    })();
  }, []);

  const unmatched = useMemo(
    () => transactions.filter((tx) => !tx.reference || !invoices.some((inv) => inv.invoice_number === tx.reference)),
    [transactions, invoices],
  );

  async function match(txId: string): Promise<void> {
    const invoiceId = selectedInvoices[txId];
    if (!invoiceId) {
      setError('Please select an invoice first.');
      return;
    }

    setBusyTxId(txId);
    setError(null);
    setMessage(null);
    try {
      await apiPost<{ status: string }>(`/api/wise/match/${txId}`, { invoice_id: invoiceId });
      setTransactions((prev) => prev.filter((tx) => tx.id !== txId));
      setMessage(`Matched ${txId} to invoice ${invoiceId}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to match transaction');
    } finally {
      setBusyTxId(null);
    }
  }

  return (
    <div>
      <h1 className="text-xl font-semibold">Reconciliation</h1>
      <p className="mt-1 text-sm text-slate-600">Unmatched Wise transfers and manual invoice matching.</p>

      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      {message ? <p className="mt-3 text-sm text-emerald-700">{message}</p> : null}

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-slate-600">
              <th className="py-2 pr-3">Transfer</th>
              <th className="py-2 pr-3">Amount</th>
              <th className="py-2 pr-3">Reference</th>
              <th className="py-2 pr-3">Occurred</th>
              <th className="py-2">Manual match</th>
            </tr>
          </thead>
          <tbody>
            {unmatched.map((tx) => (
              <tr key={tx.id} className="border-b border-slate-100 align-top">
                <td className="py-2 pr-3">
                  <p className="font-medium text-slate-900">{tx.id}</p>
                  <p className="text-xs text-slate-500">{tx.sender_name ?? '-'}</p>
                </td>
                <td className="py-2 pr-3">{formatMoney(tx.amount, tx.currency)}</td>
                <td className="py-2 pr-3">{tx.reference ?? '-'}</td>
                <td className="py-2 pr-3">{shortDate(tx.occurred_at)}</td>
                <td className="py-2">
                  <div className="flex items-center gap-2">
                    <select
                      className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                      value={selectedInvoices[tx.id] ?? ''}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSelectedInvoices((prev) => ({ ...prev, [tx.id]: value }));
                      }}
                    >
                      <option value="">Select invoice</option>
                      {invoices.map((invoice) => (
                        <option key={invoice.id} value={invoice.id}>
                          {invoice.invoice_number} ({invoice.status})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        void match(tx.id);
                      }}
                      disabled={busyTxId === tx.id}
                      className="rounded-md border border-brand-300 px-3 py-1 text-sm text-brand-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {busyTxId === tx.id ? 'Matching...' : 'Match'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(value);
}

function shortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toISOString().slice(0, 10);
}
